import { FIELDS as EN_US_FIELDS } from "./langs/en-US";
import { FIELDS as DE_DE_FIELDS } from "./langs/de-DE";
import { FIELDS as ES_ES_FIELDS } from "./langs/es-ES";
import { FIELDS as FR_FR_FIELDS } from "./langs/fr-FR";
import { FIELDS as IT_IT_FIELDS } from "./langs/it-IT";
import { FIELDS as JP_JP_FIELDS } from "./langs/jp-JP";
import { FIELDS as NL_NL_FIELDS } from "./langs/nl-NL";
import { FIELDS as PT_BR_FIELDS } from "./langs/pt-BR";
import { FIELDS as PT_PT_FIELDS } from "./langs/pt-PT";

type NestedPaths<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends object
          ? `${K}.${NestedPaths<T[K]>}`
          : K
        : never;
    }[keyof T]
  : never;

export type LocaleKey = keyof typeof TRANSLATIONS;
export type TranslationPaths = NestedPaths<(typeof TRANSLATIONS)[LocaleKey]>;

/**
 * An object containing translations for different locales.
 */
const FALLBACK_LOCALE = "en-US" as const satisfies LocaleKey;

const TRANSLATIONS = {
  "en-US": {
    common: {
      example: {
        hello: "Hello World!",
      },
    },
    fields: EN_US_FIELDS,
  },
  "de-DE": {
    common: {
      example: {
        hello: "Hallo Welt!",
      },
    },
    fields: DE_DE_FIELDS,
  },
  "es-ES": {
    common: {
      example: {
        hello: "¡Hola Mundo!",
      },
    },
    fields: ES_ES_FIELDS,
  },
  "fr-FR": {
    common: {
      example: {
        hello: "Bonjour le monde!",
      },
    },
    fields: FR_FR_FIELDS,
  },
  "it-IT": {
    common: {
      example: {
        hello: "Ciao Mondo!",
      },
    },
    fields: IT_IT_FIELDS,
  },
  "jp-JP": {
    common: {
      example: {
        hello: "こんにちは世界！",
      },
    },
    fields: JP_JP_FIELDS,
  },
  "nl-NL": {
    common: {
      example: {
        hello: "Hallo Wereld!",
      },
    },
    fields: NL_NL_FIELDS,
  },
  "pt-BR": {
    common: {
      example: {
        hello: "Olá Mundo!",
      },
    },
    fields: PT_BR_FIELDS,
  },
  "pt-PT": {
    common: {
      example: {
        hello: "Olá Mundo!",
      },
    },
    fields: PT_PT_FIELDS,
  },
} as const;

export type CommonCopy = {
  errors: Record<string, string>;
};

type FieldDictionary = typeof EN_US_FIELDS;

export type FieldKey = keyof FieldDictionary;
export type FieldCopy = FieldDictionary[FieldKey];

export function getFieldDictionary(locale: LocaleKey): FieldDictionary {
  return (TRANSLATIONS[locale]?.fields ??
    TRANSLATIONS[FALLBACK_LOCALE].fields) as FieldDictionary;
}

export function getFieldCopy(locale: LocaleKey, field: FieldKey): FieldCopy {
  const dictionary = getFieldDictionary(locale);
  if (dictionary[field]) {
    return dictionary[field];
  }

  return TRANSLATIONS[FALLBACK_LOCALE].fields[field] as FieldCopy;
}

/**
 * An object containing translations for different locales.
 * Each locale key maps to its respective translation object.
 *
 * @returns An array of available locale keys from the translations object.
 */
export function getLocales() {
  return Object.keys(TRANSLATIONS) as LocaleKey[];
}

/**
 * Retrieves a translation string for the specified locale and translation path.
 *
 * @template T - The locale key type that extends LocaleKey
 * @param locale - The locale identifier (e.g., "en-US", "fr-FR")
 * @param path - The dot-separated path to the translation key (e.g., "common.buttons.save")
 * @returns The translated string, or falls back to en-US if available, or returns the path if no translation is found
 *
 * @example
 * ```typescript
 * const greeting = t("en-US", "common.greetings.hello");
 * const buttonText = t("fr-FR", "buttons.submit");
 * ```
 *
 * @remarks
 * - If the translation is not found in the requested locale, the function will attempt to fall back to "en-US"
 * - If no translation is found in either the requested locale or the fallback, a warning is logged and the path is returned
 * - Uses dynamic property access to traverse nested translation objects
 */
export function t<T extends LocaleKey>(
  locale: T,
  path: TranslationPaths,
): string {
  const keys = path.split(".");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Using any to allow dynamic access to nested properties
  let value: any = TRANSLATIONS[locale];

  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      // If not en-US, try fallback to en-US first
      if (locale !== "en-US") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Using any to allow dynamic access to nested properties
        let fallbackValue: any = TRANSLATIONS["en-US"];
        for (const fallbackKey of keys) {
          fallbackValue = fallbackValue[fallbackKey];
          if (fallbackValue === undefined) {
            break;
          }
        }
        if (fallbackValue !== undefined) {
          return fallbackValue;
        }
      }

      console.warn(`Translation for "${path}" not found in locale "${locale}"`);
      return path; // Return the path if translation is not found
    }
  }

  return value;
}
